import { useContext, useEffect, useRef, useState } from 'react';

import BarraInferiorBotoes from '../../../../../components/BarraInferiorBotoes';
import BoxConteudo from '../../../../../components/BoxConteudo';
import CustomButton from '../../../../../components/CustomButton';
import CustomCol from '../../../../../components/CustomCol';
import { CustomSwal } from '../../../../../components/CustomFeedback/CustomSwal';
import { useToast } from '../../../../../components/CustomFeedback/CustomToast';
import CustomInputDate, {
  CustomInputDateRef,
} from '../../../../../components/CustomInputs/CustomInputDate';
import CustomInputText from '../../../../../components/CustomInputs/CustomInputText';
import CustomModal from '../../../../../components/CustomModal';
import CustomRow from '../../../../../components/CustomRow';
import CustomMultiSelect from '../../../../../components/CustomSelects/CustomMultiSelect';
import CustomSelect from '../../../../../components/CustomSelects/CustomSelect';
import CustomTable from '../../../../../components/CustomTable';
import { TableRow } from '../../../../../components/CustomTable/types/table.type';
import Log from '../../../../../components/Log/Log';
import WorkspaceBotoes from '../../../../../components/WorkspaceBotoes';
import WorkspaceFicha from '../../../../../components/WorkspaceFicha';
import { UserContext } from '../../../../../modules/contexts/Auth/UserProvider';
import {
  getStatusAtivoMedsusOptions,
  StatusAtivoMedsusEnum,
} from '../../../../../modules/types/StatusAtivoMedsus.enum';
import { LogData } from '../../../../../modules/types/types';
import { useCid } from '../../../../../services/enum/medsus/useCid';
import { isHttpError } from '../../../../../services/httpClient';
import {
  getAllMedsusCidByDiretrizVmp,
  handleCreateMedsusCid,
  handleUpdateMedsusCid,
} from '../../../../../services/medsus/medsusCidService';
import { useStatusCid } from '../../../../../services/medsus/useStatusCid';
import theme from '../../../../../theme';
import { ENTITY } from '../../../../../utils/entityNames';
import { sanitizarForm } from '../../../../../utils/functions';
import { handleDelete } from '../../../../../utils/handleDelete';
import { headersCIDMedsus } from '../auxliarProdutoMedsus';
import { INITIAL_FORM_PRODUTO_MEDSUS_CID } from '../utils/initialStates';
import { MedsusCID } from '../utils/medsusTypes';

interface CidProps {
  vmpID: string;
  userID: number;
  btDisable?: boolean;
}

export function CidMedsus(props: CidProps) {
  const { cudProdutoDaf } = useContext(UserContext);
  const entity = ENTITY.ENTITY_Medsus_Cid;

  // Enums
  const statusCid = useStatusCid();
  const { cidOptions, descricaoOptions } = useCid();

  // Data
  const [updating, setUpdating] = useState<boolean>(false);
  const [cidData, setCidData] = useState<MedsusCID[]>([]);
  const [cidSelecionados, setCidSelecionados] = useState<string[]>([]);
  const [form, setForm] = useState(
    INITIAL_FORM_PRODUTO_MEDSUS_CID(
      props.userID,
      Number(props.vmpID),
    ) as MedsusCID,
  );
  const [showModalCid, setShowModalCid] = useState<boolean>(false);

  const [showModalSelecaoCid, setShowModalSelecaoCid] =
    useState<boolean>(false);

  const [logData, setLogData] = useState<LogData>();

  // Hooks
  const { showToast } = useToast();
  const dataExclusaoRef = useRef<CustomInputDateRef>(null);

  useEffect(() => {
    if (isProtocoloCreated()) fetchData();
  }, [props.vmpID]);

  // Data Management
  const isProtocoloCreated = (): boolean => {
    return Number(props.vmpID) !== 0;
  };

  const fetchData = async () => {
    try {
      const objetos = await getAllMedsusCidByDiretrizVmp(props.vmpID);

      setCidData(objetos);
    } catch (error) {
      console.error('Erro ao obter dados: ', error);
    }
  };

  const tableData: TableRow[] = cidData?.map((medsusCID: MedsusCID) => ({
    id: medsusCID.id,
    status: statusCid[Number(medsusCID.status)]?.label ?? '',
    cid: medsusCID.cid?.cid ?? '',
    descricao: medsusCID.cid?.descricao ?? '',
  }));

  // Form Validate
  const validateForm = (form: MedsusCID) => {
    const errors: string[] = [];

    if (!form.status) errors.push('<b>Status</b> é obrigatório.');

    if (!form.dtInclusaoDiretriz)
      errors.push('<b>Data Inclusão</b> é obrigatório.');

    // if (!form.cidId) errors.push('<b>CID</b> é obrigatório.');

    if (!handleDataExclusaoValidate() && !form.dtExclusaoDiretriz)
      errors.push('<b>Data Exclusão</b> é obrigatório.');

    if (errors.length > 0) {
      CustomSwal.quick('warning', errors.join('<br>')); // Exibe todas as mensagens de erro no swal
      return false;
    }

    return true; // Validação bem-sucedida
  };

  const formatFormCreate = (cidIdSelecionado: number) => {
    const formAjustado = {
      ...form,
      status: StatusAtivoMedsusEnum['ATIVO'],
      cidId: cidIdSelecionado,
      dtInclusaoDiretriz: new Date(),
    };

    return formAjustado;
  };

  const formatForm = (form: any) => {
    const formAjustado = {
      ...form,
      status: Number(form.status),
      cidId: Number(form.cidId),
    };

    return formAjustado;
  };

  // Handle Functions
  const handleDataExclusaoValidate = (): boolean => {
    if (Number(form.status) !== StatusAtivoMedsusEnum['INATIVO']) {
      return true;
    } else {
      return false;
    }
  };

  const handleClearForm = () => {
    setForm(INITIAL_FORM_PRODUTO_MEDSUS_CID(props.userID, Number(props.vmpID)));
    setLogData({
      ...logData,
      id: 0,
      usuarioRegistro: '',
      usuarioAtualizacao: '',
      dataRegistro: '',
      dataAtualizacao: '',
      nEdicoes: 0,
    });
  };

  const handleClearFormSelecao = () => {
    setCidSelecionados([]);
  };

  const handleRowClick = (row: TableRow) => {
    handleShowModalCid();
    setUpdating(true);
    const filteredItem = cidData.find((item) => item.id == row.id);

    setLogData({
      id: filteredItem?.id ?? 0,
      usuarioRegistro: filteredItem?.usuarioRegistro?.nomeCompleto || '',
      usuarioAtualizacao: filteredItem?.usuarioAtualizacao?.nomeCompleto || '',
      dataRegistro: filteredItem?.dataRegistro || '',
      dataAtualizacao: filteredItem?.dataUltimaAtualizacao || '',
      nEdicoes: filteredItem?.nEdicoes || 0,
    });
    if (filteredItem) {
      setForm({
        ...form,
        id: filteredItem.id,

        status: filteredItem.status,
        dtInclusaoDiretriz: filteredItem.dtInclusaoDiretriz,
        dtExclusaoDiretriz: filteredItem.dtExclusaoDiretriz,

        diretrizVmp: filteredItem.diretrizVmp ?? undefined,
        diretrizVmpId: filteredItem.diretrizVmp?.id ?? Number(props.vmpID),
        cid: filteredItem.cid ?? undefined,
        cidId: filteredItem.cid?.id ?? 0,

        //Observações
        observacoes: filteredItem.observacoes,
        usuarioId: Number(props.userID),
      });
    }
  };

  const handleShowModalCid = () => {
    setShowModalCid((prev) => !prev);
    handleClearForm();
  };

  const handleShowModalSelecaoCid = () => {
    setShowModalSelecaoCid((prev) => !prev);
    handleClearFormSelecao();
  };

  const handleChangeCid = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = event.target;

    if (name === 'status') {
      setForm((prev) => ({
        ...prev,
        dtExclusaoDiretriz:
          value === '2' ? new Date(Date.now()).toISOString() : undefined,
      }));
    }
    setForm((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleClickInserir = () => {
    handleShowModalSelecaoCid();
    setUpdating(false);
  };

  // REST
  const handleCreateCid = async () => {
    cidSelecionados.forEach(async (cid) => {
      try {
        const formAjustado = formatFormCreate(Number(cid));
        const formToSend = sanitizarForm(formAjustado);

        await handleCreateMedsusCid(formToSend);

        showToast('Registro criado com sucesso!', 'success', 5000);
        fetchData();
        setTimeout(() => handleShowModalSelecaoCid(), 1000);
      } catch (error: any) {
        if (isHttpError(error)) {
          showToast(error.response?.data.message, 'error');
        } else {
          showToast(error.message, 'error');
        }
        console.error('Erro ao salvar registro:', error);
      }
    });
  };

  const handleUpdateCid = async () => {
    try {
      if (!validateForm(form)) throw new Error('Formulário inválido');

      const formAjustado = formatForm(form);
      const formToSend = sanitizarForm(formAjustado);

      const response = await handleUpdateMedsusCid(formToSend);

      showToast(response.message);
      fetchData();
    } catch (error: any) {
      if (isHttpError(error)) {
        showToast(error.response?.data.message, 'error');
      }
      console.error('Erro ao atualizar dados', error);
    }
  };

  const handleDeleteCid = async () => {
    await handleDelete(form.id, 'medsuscid');
    fetchData();
    handleShowModalCid();
  };

  return (
    <BoxConteudo boxTitulo="Lista de CID-10" fechado>
      <CustomRow>
        <CustomCol width="100%">
          <CustomTable
            headers={headersCIDMedsus}
            data={tableData}
            onRowClick={handleRowClick}
            showPagination={false}
          />
        </CustomCol>
      </CustomRow>

      {cudProdutoDaf && (
        <CustomCol style={{ marginTop: '15px', textAlign: 'right' }}>
          <CustomButton
            label="Novo CID-10"
            bgColor={theme.colors.botaoAcao.azul}
            textColor={theme.colors.white}
            onClick={handleClickInserir}
            disabled={!isProtocoloCreated()}
          />
        </CustomCol>
      )}

      {/* Modal CID */}
      <CustomModal
        title="CID-10"
        open={showModalCid}
        onHide={handleShowModalCid}
        icon="list_alt"
        width="65%"
      >
        <WorkspaceFicha>
          <Log {...logData} entity={entity} />
          <BoxConteudo boxTitulo="Identificação do CID-10">
            <CustomRow
              style={{
                marginTop: '-15px',
                justifyContent: 'flex-start',
              }}
            >
              <CustomCol width="200px">
                <CustomSelect
                  name="status"
                  options={getStatusAtivoMedsusOptions()}
                  title="Status"
                  value={form.status}
                  onChange={handleChangeCid}
                  required
                />
              </CustomCol>
              <CustomCol width="250px">
                <CustomInputDate
                  name="dtInclusaoDiretriz"
                  title="Data Inclusão"
                  value={form.dtInclusaoDiretriz}
                  onChange={handleChangeCid}
                  required
                />
              </CustomCol>
              <CustomCol width="250px">
                <CustomInputDate
                  name="dtExclusaoDiretriz"
                  title="Data Inativação"
                  value={form.dtExclusaoDiretriz}
                  onChange={handleChangeCid}
                  readOnly={handleDataExclusaoValidate()}
                  required={!handleDataExclusaoValidate()}
                  ref={dataExclusaoRef}
                  minDate={
                    form.dtInclusaoDiretriz
                      ? new Date(form.dtInclusaoDiretriz)
                      : undefined
                  }
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol width="200px">
                <CustomInputText
                  name="cidId"
                  title="Código CID-10"
                  value={form.cid?.cid ?? ''}
                  onChange={handleChangeCid}
                  readOnly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="descricao"
                  title="Descrição"
                  value={form.cid?.descricao ?? ''}
                  onChange={handleChangeCid}
                  type="textArea"
                  rows={3}
                  readOnly
                />
              </CustomCol>
            </CustomRow>
            <CustomRow>
              <CustomCol>
                <CustomInputText
                  name="observacoes"
                  title="Observações"
                  value={form.observacoes ?? ''}
                  type="textArea"
                  onChange={handleChangeCid}
                  rows={3}
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            {updating ? (
              <WorkspaceBotoes
                delete
                deleteDisabled={!cudProdutoDaf}
                onClickDelete={handleDeleteCid}
                save
                // saveDisabled={!props.btDisable}
                onClickSave={handleUpdateCid}
                saveDisabled={!cudProdutoDaf}
              />
            ) : (
              <WorkspaceBotoes
                save
                saveDisabled={!cudProdutoDaf}
                // saveDisabled={!props.btDisable}
                onClickSave={handleCreateCid}
              />
            )}
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomModal>

      {/* Modal CID */}
      <CustomModal
        title="Selecione os CID-10"
        open={showModalSelecaoCid}
        onHide={handleShowModalSelecaoCid}
        icon="list_alt"
        width="65%"
      >
        <WorkspaceFicha>
          {logData?.id ? <Log {...logData} entity={entity} /> : null}
          <BoxConteudo boxTitulo="Seleção de CID-10">
            <CustomRow
              style={{
                marginTop: '-15px',
                justifyContent: 'flex-start',
              }}
            >
              <CustomCol width="30%">
                <CustomMultiSelect
                  title="Código"
                  options={cidOptions}
                  value={cidOptions.filter((cidOption) =>
                    cidSelecionados.includes(cidOption.value),
                  )}
                  onChange={(selected) =>
                    setCidSelecionados(selected.map((s) => s.value))
                  }
                />
              </CustomCol>
              <CustomCol width="70%">
                <CustomMultiSelect
                  title="Descrição"
                  options={descricaoOptions}
                  value={descricaoOptions.filter((cidOption) =>
                    cidSelecionados.includes(cidOption.value),
                  )}
                  onChange={(selected) =>
                    setCidSelecionados(selected.map((s) => s.value))
                  }
                />
              </CustomCol>
            </CustomRow>
          </BoxConteudo>
          <BarraInferiorBotoes>
            <WorkspaceBotoes
              save
              saveDisabled={!isProtocoloCreated || !cudProdutoDaf}
              onClickSave={handleCreateCid}
            />
          </BarraInferiorBotoes>
        </WorkspaceFicha>
      </CustomModal>
    </BoxConteudo>
  );
}
